"use client";
// react imports
import React, { useEffect } from "react";
// material ui imports
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
// next imports
import NextLink from "next/link";
import { usePathname } from "next/navigation";
// Json imports
import sidebarData from "@/utils/json/featuresLoader";
// context imports
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { useReadyForOrdersLabel, READY_FOR_ORDERS_FEATURE_KEY } from "@/hooks/useReadyForOrdersLabel";
import { useVendorManagementLabel, VENDOR_MANAGEMENT_FEATURE_KEY } from "@/hooks/useVendorManagementLabel";
import { useShoppingListFormReset } from "@/components/ShoppingListManagementComponent/hooks/useShoppingListFormReset";
// utility imports
import { decodeHtmlEntities } from "@/utils/helper";
// scss imports
import "./bread-crumbs.scss"

// Move CustomSeparator outside component to avoid creating it during render
const CustomSeparator: React.FC = () => {
  return <span className="text-TJ-Almost-Black dark:text-TJ-white">/</span>; // Tailwind classes for styling
};

function pushUniqueBreadcrumb(
  items: { title: string; path: string }[],
  item: { title: string; path: string }
) {
  const alreadyExists = items.some(
    (existing) => existing.path === item.path && existing.title === item.title
  );
  if (!alreadyExists) {
    items.push(item);
  }
}

const DO_NOT_DO_BUSINESS_SUBMENU_KEY = "doNotDoBusiness";

const Breadcrumb: React.FC = () => {
  const { mainSidebarItems } = sidebarData;
  const pathname = usePathname();
  const { languageText } = useLanguageContext();
  const { showLastBreadcrumb, setShowProductInfo, setShowCreateProduct, selectedIdeaName, showProductInfo, setShouldFetchShoppingList, selectedNodePath } = useGlobalStateContext();
  const { clearProductSession } = useShoppingListFormReset();
  const readyForOrdersLabel = useReadyForOrdersLabel();
  const vendorManagementLabel = useVendorManagementLabel();
  const doNotDoBusinessBreadcrumbTitle =
    languageText?.PAGE_TITLES?.DO_NOT_DO_BUSINESS_BREADCRUMB ?? "Required Business Review Approval";

  const resolveSubmenuBreadcrumbTitle = (menuKey: string, submenuKey: string, submenuName: string) => {
    if (menuKey === READY_FOR_ORDERS_FEATURE_KEY) {
      return readyForOrdersLabel.submenuName;
    }
    if (submenuKey === DO_NOT_DO_BUSINESS_SUBMENU_KEY) {
      return doNotDoBusinessBreadcrumbTitle;
    }
    return submenuName;
  };

  // Utility function to find the breadcrumb path based on the current route
  const findBreadcrumbItems = (): { title: string; path: string }[] => {
    let breadcrumbItems: { title: string; path: string }[] = [];

    breadcrumbItems.push({ title: "Home", path: "/home" });

    mainSidebarItems.forEach((item) => {
      const menuTitle =
        item.key === READY_FOR_ORDERS_FEATURE_KEY
          ? readyForOrdersLabel.title
          : item.key === VENDOR_MANAGEMENT_FEATURE_KEY
            ? vendorManagementLabel.title
            : item.title;

      // Check if the current path matches the main path exactly or starts with the pathPrefix
      if (pathname === item.path || pathname.startsWith(item.pathPrefix)) {
        const firstSubmenuPath = item.submenu?.[0]?.submenuPath;
        const parentPath =
          item.path ?? (firstSubmenuPath ? `${item.pathPrefix}${firstSubmenuPath}` : `${item.pathPrefix}/`);
        pushUniqueBreadcrumb(breadcrumbItems, { title: menuTitle, path: parentPath });

        // If there are submenus, check if the path matches any submenu
        item.submenu?.forEach((submenuItem) => {
          const fullPath = `${item.pathPrefix}${submenuItem.submenuPath}`;
          // Ignore placeholder submenu paths ('/') and empty paths ('') to prevent matching every child route
          if (submenuItem.submenuPath === "/" || submenuItem.submenuPath === "") return;

          if (pathname.startsWith(fullPath)) {
            pushUniqueBreadcrumb(breadcrumbItems, {
              title: resolveSubmenuBreadcrumbTitle(item.key, submenuItem.key, submenuItem.name),
              path: fullPath,
            });
          }
        });
      } else {
        // Check if the pathname matches any submenu path directly (for cases where submenuPath is the full path)
        item.submenu?.forEach((submenuItem) => {
          if (submenuItem.submenuPath !== "/" && submenuItem.submenuPath !== "" && pathname === submenuItem.submenuPath) {
            // Add the parent menu item first
            pushUniqueBreadcrumb(breadcrumbItems, { title: menuTitle, path: item.pathPrefix + "/" });
            // Then add the submenu item
            pushUniqueBreadcrumb(breadcrumbItems, {
              title: resolveSubmenuBreadcrumbTitle(item.key, submenuItem.key, submenuItem.name),
              path: submenuItem.submenuPath,
            });
          }
        });
      }

    });
    if (showLastBreadcrumb.remove) {
      const lastBreadcrumb = breadcrumbItems.length > 0 ? breadcrumbItems[breadcrumbItems.length - 1] : undefined;
      if (lastBreadcrumb?.title == showLastBreadcrumb.title) {
        breadcrumbItems = breadcrumbItems.slice(0, breadcrumbItems.length - 1)
      }
    } else {
      // Only add showLastBreadcrumb if it's not already in the breadcrumbItems
      if (showLastBreadcrumb?.title && showLastBreadcrumb.title != "") {
        const isAlreadyInBreadcrumbs = breadcrumbItems.some(item => item.title === showLastBreadcrumb.title);
        if (!isAlreadyInBreadcrumbs) {
          breadcrumbItems.push({ title: showLastBreadcrumb.title, path: showLastBreadcrumb.path });
        }
      }
    }
    return breadcrumbItems;
  };

  const breadcrumbItems = findBreadcrumbItems();

  // if it is product information page we are appending idea name to breadcrumb.
  if (showProductInfo && selectedIdeaName != "") {
    breadcrumbItems.push({ title: decodeHtmlEntities(selectedIdeaName), path: "" })
  }

  // Before returning from Breadcrumb component
  if (selectedNodePath.length > 0) {
    selectedNodePath.forEach(node => {
      breadcrumbItems.push({
        title: decodeHtmlEntities(node.name),
        path: "",
      });
    });
  }

  // showing the product list
  const handleBreadcrumbClick = (title: string, path: string) => {
    if (path === '/create-product/shopping-list-management') {
      clearProductSession();
      setShowCreateProduct(false);
      setShowProductInfo(false);
      setShouldFetchShoppingList(true)
    }
  };

  useEffect(() => {
  }, [showLastBreadcrumb])

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator={<CustomSeparator />}
      className="py-6 text-TJ-Almost-Black flex items-center overflow-x-auto"
      style={{ whiteSpace: 'nowrap' }}
    >
      {breadcrumbItems.map((item, index) =>
        index === breadcrumbItems.length - 1 ? (
          // Set the last breadcrumb item's text to bold
          <Typography
            key={`breadcrumb-item-` + item.title}
            className="dark:text-TJ-white whitespace-nowrap flex-shrink-0"
            sx={{
              fontSize: '.875rem',
              fontWeight: 700,
              lineHeight: '1.25rem',
              fontFamily: 'Cerebri Sans',
              color: "#2C2C2C",
            }}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ) : (
          <NextLink
            key={`breadcrumb-item-` + item.title}
            href={item.path} passHref onClick={() => handleBreadcrumbClick(item.title, item.path)}>
            <Typography
              className="dark:text-TJ-white whitespace-nowrap flex-shrink-0"
              sx={{
                fontSize: '.875rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                fontFamily: 'Cerebri Sans',
                color: "#2C2C2C",
              }}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          </NextLink>
        )
      )}
    </Breadcrumbs>
  );
};

export default Breadcrumb;
